import { FormHelperText, Stack, Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { ApiRequest_UpdatePhone_User, useUpdatePhone } from '@/api/endpoints/user/useUpdatePhone';
import { useAuth } from '@/context/AuthProvider';

import FormTextField from '@/components/form/FormTextField';

const PhoneForm: React.FC = () => {
  const { user } = useAuth();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ApiRequest_UpdatePhone_User>({
    defaultValues: {
      phone: user?.phone ?? '',
    },
  });

  const [formError, setFormError] = useState<Error | null>(null);

  const updatePhoneMutation = useUpdatePhone({
    onError: error => {
      setFormError(error);
    },
  });

  // Function to format phone number to standard format
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Handle different cases
    if (digitsOnly.length === 10) {
      // US number without country code: 1234567890 -> 1234567890
      return digitsOnly;
    } if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      // US number with country code: 11234567890 -> 1234567890
      return digitsOnly.slice(1);
    } if (digitsOnly.length >= 10) {
      // International or other format, take last 10 digits
      return digitsOnly.slice(-10);
    }
    
    // If less than 10 digits, return as is (will be caught by validation)
    return digitsOnly;
  };

  const onSubmit = useCallback(
    (data: ApiRequest_UpdatePhone_User) => {
      setFormError(null);
      
      // Format the phone number before sending
      const formattedData = {
        ...data,
        phone: formatPhoneNumber(data.phone),
      };
      
      return updatePhoneMutation.mutateAsync(formattedData);
    },
    [updatePhoneMutation]
  );

  return (
    <Stack spacing={1} component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Your Phone Number
          </Typography>
          <FormTextField
            placeholder="Enter your phone number in any format"
            size="small"
            error={!!errors.phone}
            helperText={errors.phone?.message?.toString()}
            control={control}
            required
            sx={{
              width: 200,
              '& .MuiInputBase-root': {
                height: 40,
              },
              '& .MuiInputBase-input': {
                height: '40px',
                padding: '0 8px',
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
              },
            }}
            {...register('phone', {
              required: 'Phone number is required',
              validate: (value) => {
                const digitsOnly = value.replace(/\D/g, '');
                if (digitsOnly.length < 10) {
                  return 'Phone number must have at least 10 digits';
                }
                return true;
              },
            })}
          />
        </Box>

        <LoadingButton
          loading={isSubmitting}
          variant="contained"
          type="submit"
          size="small"
          sx={{ height: 40 }}
        >
          Update Phone Number
        </LoadingButton>
      </Box>

      {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
    </Stack>
  );
};

export default PhoneForm;
